import React, { FunctionComponent, useEffect, useState } from 'react'
import validator from 'validator'
import deepEqual from 'fast-deep-equal'
import { api } from 'core/api/ConnectionManager'
import translate from '../../core/translate'
import styles from './styles'
import ProfileAvatar from './ProfileAvatar'

import ProfileForm from './ProfileForm'
import { Icon } from '../../molecules/Icon'
import helpers from '../../core/helpers'
import { mediaJS } from 'theme'
import { IFindAdUserRequest, IFindAdUserResponse, ProfileProps, ProfileRole, ProfileUserState } from './Profile-types'
import { emitError, emitAppError } from 'features/appNotifications/AppNotifications.state'
import { useDispatch } from 'react-redux'
import { updateUser, createUser, IUserMutationPayload } from 'blocks.app/user/user.state'
import { AppDispatch } from 'core/store'
import { isPasswordCompositionValid } from './passwordValidation'

const PASSWORD_MIN_LENGTH = 12

const INITIAL_USER_STATE: ProfileUserState = {
    email: '',
    password: '',
    oldPassword: '',
    authType: 'smartplayer',
    name: '',
    passwordRepeat: '',
    roles: [null],
    file: null,
    photo: undefined,
    adUser: null,
    userId: undefined,
    activeBefore: null,
}

const isProfileRole = (role: ProfileRole): role is Exclude<ProfileRole, null> => role !== null

const Profile: FunctionComponent<ProfileProps> = (p_) => {
    const dispatch = useDispatch<AppDispatch>()
    const [user, setUser] = useState<ProfileUserState>(INITIAL_USER_STATE)
    const [compareUserData, setCompareUserData] = useState<ProfileUserState>(INITIAL_USER_STATE)
    const [isActiveDirectory, setIsActiveDirectory] = useState<boolean | undefined>(p_.isActiveDirectory)

    useEffect(() => {
        if (!p_.open) {
            return
        }

        const userData: ProfileUserState = {
            ...INITIAL_USER_STATE,
        }

        if (p_.user) {
            userData.userId = p_.user.id
            userData.roles = p_.user.roles?.length ? p_.user.roles.map((role) => ({ value: role.id, ...role })) : [null]
            userData.name = p_.user.name || ''
            userData.email = p_.user.email
            userData.photo = p_.user.photo
            userData.authType = p_.user.authType

            if (p_.user.authType === 'activeDirectory') {
                userData.adUser = {
                    error: false,
                }
            }
        }

        setCompareUserData(userData)
        setUser(userData)
        setIsActiveDirectory(p_.isActiveDirectory)
    }, [p_.open, p_.user, p_.isActiveDirectory])

    const onClose = () => {
        _closeModal()
    }

    const onReject = () => {
        _closeModal()
    }

    const validate = (): boolean => {
        const isEdit = user.userId

        if (!isActiveDirectory && !user.email) {
            dispatch(emitError('emailEmpty'))
            return false
        }

        const roles = user.roles.filter(isProfileRole)

        if (_isRolesEmpty(roles)) {
            dispatch(emitError('selectAtLeastOneRole'))
            return false
        }

        if (!isActiveDirectory && !_isValidateEmailFormat(user.email)) {
            dispatch(emitError('emailFormatError'))
            return false
        }

        if (_isSmartPlayerAuth()) {
            if (isEdit) {
                return _isValidateForEditMode()
            }
            return _isValidForCreateMode()
        }

        return true
    }
    const _isRolesEmpty = (roles: ProfileRole[]): boolean => {
        return roles.length === 0
    }
    const _isValidForCreateMode = (): boolean => {
        if (!user.password) {
            dispatch(emitError('passwordEmpty'))
            return false
        }

        if (user.password !== user.passwordRepeat) {
            dispatch(emitError('repeatPasswordError'))
            return false
        }

        if (user.password.length < PASSWORD_MIN_LENGTH) {
            dispatch(
                emitAppError({
                    errorCode: `${translate('passwordMinLength')}: ${PASSWORD_MIN_LENGTH}`,
                    message: `: ${PASSWORD_MIN_LENGTH}`,
                })
            )
            return false
        }

        if (!isPasswordCompositionValid(user.password)) {
            dispatch(emitError('passwordRequirements'))
            return false
        }

        return true
    }
    const _isValidateForEditMode = (): boolean => {
        if (_isPasswordChange()) {
            if (user.password !== user.passwordRepeat) {
                dispatch(emitError('repeatPasswordError'))
                return false
            }

            if (user.password.length < PASSWORD_MIN_LENGTH) {
                dispatch(
                    emitAppError({
                        errorCode: `${translate('passwordMinLength')}: ${PASSWORD_MIN_LENGTH}`,
                        message: `: ${PASSWORD_MIN_LENGTH}`,
                    })
                )
                return false
            }

            if (!isPasswordCompositionValid(user.password)) {
                dispatch(emitError('passwordRequirements'))
                return false
            }

            if (p_.owner && user.password && !user.oldPassword) {
                dispatch(emitError('enterOldPassword'))
                return false
            }
        }

        return true
    }
    const _isPasswordChange = () => {
        return user.password !== '' || user.passwordRepeat !== ''
    }
    const _isValidateEmailFormat = (checkEmail: string): boolean => {
        return validator.isEmail(checkEmail)
    }
    const onAccept = () => {
        if (deepEqual(user, compareUserData)) {
            return
        }

        const isValid = validate()

        if (!isValid) {
            return
        }

        const isEdit = !!user.userId
        const userData: IUserMutationPayload = {
            name: user.name,
            email: user.email,
            activeBefore: user.activeBefore,
        }

        if (!p_.owner) {
            userData.roleIds = JSON.stringify(user.roles.filter(isProfileRole).map((role) => role.value))
        }

        if (isEdit) {
            userData.userId = user.userId

            if (p_.owner && user.password && _isSmartPlayerAuth()) {
                userData.oldPassword = user.oldPassword
            }
        }

        if (_isSmartPlayerAuth()) {
            if (user.password) {
                userData.password = user.password
            }
        }

        if (user.file) {
            userData.file = user.file
        }

        if (user.authType === 'activeDirectory') {
            userData.authType = user.authType
        }

        if (isEdit) {
            dispatch(updateUser(userData))
            _closeModal()
            return
        }

        dispatch(createUser(userData))
        _closeModal()
    }

    const _closeModal = () => {
        p_.onClose()
    }

    const _isSmartPlayerAuth = (): boolean => {
        return user.authType === 'smartplayer'
    }

    const onChangeAvatar = (file: File) => {
        setUser((currentUser) => ({ ...currentUser, file }))
    }

    const findAdUser = () => {
        api.send<IFindAdUserRequest, IFindAdUserResponse>('activeDirectoryFindUserByUsername', { username: user.email })
            .then((res) => {
                setUser((currentUser) => ({
                    ...currentUser,
                    email: res.userPrincipalName,
                    name: res.displayName || '',
                    authType: 'activeDirectory',
                    adUser: {
                        error: false,
                    },
                }))
            })
            .catch(() => {
                setUser((currentUser) => ({
                    ...currentUser,
                    adUser: {
                        error: true,
                        message: translate('couldNotFindUser'),
                    },
                }))
            })
    }

    const isEdit = !!user.userId

    return (
        <React.Fragment>
            <div className={styles.profileContent}>
                <ProfileAvatar src={user.photo} onChange={onChangeAvatar} />
                <ProfileForm
                    data={user}
                    owner={p_.owner}
                    isEdit={isEdit}
                    onChange={(userData) => setUser(userData)}
                    isActiveDirectory={isActiveDirectory}
                    findAdUser={findAdUser}
                    onReject={onReject}
                    onAccept={onAccept}
                />
                <div className={styles.profileCloseIcon}>
                    <Icon
                        type="close"
                        onClick={onClose}
                        {...helpers.getPropertiesByMediaQueries(
                            {
                                desktop: { color: 'greyDark' },
                                tabMini: { color: 'greyDark' },
                            },
                            mediaJS
                        )}
                    />
                </div>
            </div>
        </React.Fragment>
    )
}

export { Profile }
